/**
 * Games tonight — the shared vote board (INVENTORY §1 screen 4, §4.2;
 * prototype lines 173-240). Default screen after login.
 *
 * Everything numeric is already derived: `selectors.tonightVM` runs the §4.2
 * aggregation through `lib/logic.ts#agg`, so this file only paints. In
 * particular the row a game lands on (the crew's rounded average call), the row
 * labels (`MUST PLAY` … `PLAY IT WITHOUT ME`), the `AVG 2.7` / `C ×2` tallies
 * and the per-user call chips all arrive pre-computed — do not re-derive any of
 * them here. `r.show` is always `true` now that every row is one of the four
 * calls; it stays read because the row VM still emits it and hiding a row is
 * the screen's decision to make, not `agg`'s.
 *
 * Two details that look like mistakes but are the prototype:
 *  - the "WHO'S PLAYING" avatars are hand-inlined 36px boxes, NOT the `Avatar`
 *    component (INVENTORY §11);
 *  - unselected chips drop to `opacity:.3` rather than being hidden.
 */

import { SearchBox } from '../components/SearchBox.tsx';
import { CoverArt } from '../components/CoverArt.tsx';
import { PixelButton } from '../components/PixelButton.tsx';
import { useArt, useQueueArt } from '../components/useArt.ts';
import { useQd } from '../store/index.ts';
import { removeChip } from '../store/selectors.3c.ts';
import { roundChipVM } from '../store/selectors.round.ts';
import { tonightVM } from '../store/selectors.ts';
import type { SharedCapsule } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';

/** One 104px board capsule. Its own component so `useArt` is one hook per tile
 *  rather than a hook inside a `.map`. Portrait ladder (INVENTORY §11: the
 *  prototype passed `g.coverBg` here). */
function Capsule({ g }: { g: SharedCapsule }): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const art = useArt(g.id + 'p', g.coverCands);

  return (
    <div
      onClick={() => openGame(g.id)}
      style={{
        position: 'relative',
        width: 104,
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid var(--gt-border-2)',
        background: 'var(--gt-tile)',
        cursor: 'pointer',
        filter: g.filt,
      }}
    >
      <div style={{ position: 'relative', aspectRatio: '2/3', background: g.fall, overflow: 'hidden' }}>
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={12} ini={g.ini} hasCover={art.hasCover} cover={art.cover} />
        <div style={{ position: 'absolute', inset: 0, background: g.sheen, mixBlendMode: 'screen', pointerEvents: 'none' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '6px 7px 8px' }}>
        <div
          style={{
            fontFamily: MONO,
            fontSize: '8.5px',
            color: 'var(--gt-text-3)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {g.nameUp}
        </div>
        <div style={{ display: 'flex', gap: 6, fontFamily: MONO, fontSize: '8.5px', whiteSpace: 'nowrap' }}>
          <span style={{ color: 'var(--acc)' }}>{g.tallyIn}</span>
          {g.hasOut ? <span style={{ color: 'var(--acc2)' }}>{g.tallyOut}</span> : null}
        </div>
        <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {g.chips.map((c) => (
            <div
              key={c.key}
              style={{
                width: 14,
                height: 14,
                flex: 'none',
                background: c.bg,
                border: c.bord,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: MONO,
                fontSize: '8.5px',
                fontWeight: 600,
                color: c.clr,
              }}
            >
              {c.t}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Tonight(): JSX.Element {
  const s = useQd();
  const v = tonightVM(s);
  const rc = roundChipVM(s);

  // The party list is this screen's whole population, and it fetches its own
  // art like every other cover-bearing screen. `revalidateVisible` already
  // covers the list at boot, on focus and on a party switch — the gap is the
  // game somebody adds MID-SESSION, which arrives as a realtime patch and was
  // then drawn from nothing but `artCands`' legacy guesses until the next
  // focus pass (the note in `components/useArt.ts`).
  //
  // `s.list`, not the filtered rows the screen renders: a search box is not a
  // reason to stop loading a cover, and the whole-list key stays stable across
  // a keystroke. The per-appid `SURFACE_MIN_MS` floor makes the overlap with
  // the boot pass free.
  useQueueArt(s.list.map((e) => e.id));

  return (
    <div data-screen-label="Games tonight" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* WHO'S PLAYING ---------------------------------------------------- */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '16px 0 0' }}>
        <div style={{ fontFamily: MONO, fontSize: '9.5px', letterSpacing: '.06em', color: 'var(--gt-muted)', padding: '0 14px' }}>
          WHO&apos;S PLAYING:
        </div>
        <div data-hs="1" style={{ display: 'flex', gap: 7, alignItems: 'center', overflowX: 'auto', padding: '2px 14px' }}>
          <span
            onClick={() => s.tfAll()}
            style={{
              border: '1px solid ' + v.tfAllBd,
              color: v.tfAllClr,
              background: v.tfAllBg,
              padding: '0 12px',
              height: 36,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              letterSpacing: '.05em',
              fontFamily: MONO,
              fontSize: 10,
              flex: 'none',
              boxSizing: 'border-box',
            }}
          >
            ALL
          </span>
          {v.tfChips.map((u) => (
            <div
              key={u.key}
              onClick={() => s.tfToggle(u.key)}
              style={{
                position: 'relative',
                width: 36,
                height: 36,
                flex: 'none',
                background: u.bg,
                border: u.bd,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'var(--gt-font-pixel)',
                fontSize: 9,
                color: 'var(--gt-ink)',
                overflow: 'hidden',
                cursor: 'pointer',
                opacity: u.op,
                transition: 'opacity .12s ease',
              }}
            >
              {u.ini}
              {u.hasAv ? <div style={{ position: 'absolute', inset: 0, background: u.avBg }} /> : null}
            </div>
          ))}
        </div>
        {v.hasTfNote ? (
          <div style={{ fontFamily: MONO, fontSize: '9.5px', color: 'var(--acc)', padding: '0 14px' }}>{v.tfNote}</div>
        ) : null}
      </div>

      {/* THE BOX SITS ABOVE THE FILTER RAIL, full width and 44px tall — the rail
          scrolls horizontally, and a field inside a horizontal scroller is not
          reachable with a thumb. Box then filter, the desktop order read top to
          bottom rather than left to right (DESIGN-CHANGELOG 2026-08-28). */}
      <div style={{ padding: '12px 14px 0' }}>
        <SearchBox
          value={v.bq}
          onChange={(e) => s.setBq('tonight', e.target.value)}
          onClear={() => s.setBq('tonight', '')}
          placeholder="SEARCH THE LIST…"
          w="100%"
          ifs={12}
          pad="13px 12px"
        />
      </div>
      {/* filter bar ------------------------------------------------------- */}
      <div data-hs="1" style={{ display: 'flex', gap: 6, alignItems: 'center', overflowX: 'auto', padding: '12px 14px 0' }}>
        <div
          onClick={() => s.toggleFltOpen()}
          style={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid ' + v.fltBtnBd,
            color: v.fltBtnClr,
            background: v.fltBtnBg,
            fontFamily: MONO,
            fontSize: '10.5px',
            height: 40,
            padding: '0 13px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flex: 'none',
            boxSizing: 'border-box',
          }}
        >
          {v.fltBtnTxt}
        </div>
        {v.fltChips.map((c) => (
          <div
            key={c.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              border: '1px solid ' + c.bd,
              color: c.clr,
              background: c.bg,
              padding: '9px 11px',
              whiteSpace: 'nowrap',
              fontFamily: MONO,
              fontSize: 10,
              flex: 'none',
            }}
          >
            {c.l}
            <span onClick={() => removeChip(s, c)} style={{ cursor: 'pointer', color: 'var(--gt-text-5)', padding: 2 }}>
              ✕
            </span>
          </div>
        ))}
      </div>

      {/* START GAME NIGHT — only when no round exists (3039, `design/GT Mobile Boards.dc.html`) */}
      {rc.rStartOn ? (
        <div style={{ padding: '12px 14px 0' }}>
          <PixelButton label="START GAME NIGHT ▸" fs={10} pad="15px 16px" center busy={s.rBusy} onClick={() => void s.rStart()} />
        </div>
      ) : null}

      {/* tier rows -------------------------------------------------------- */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 14px 26px' }}>
        {/* The board opens by asking for what only you can supply. Absent at
            zero, so a fully-called board shows nothing at all. Chip-on pattern
            at panel scale — 1px --acc over --accSoft, no new tokens. */}
        {v.hasIntNudge ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 11,
              border: '1px solid var(--acc)',
              background: 'var(--accSoft)',
              padding: '13px 14px',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--gt-font-pixel)',
                fontSize: 8,
                lineHeight: 1.5,
                color: 'var(--acc)',
              }}
            >
              {v.intNudgeHead}
            </div>
            <div
              style={{
                fontFamily: 'var(--gt-font-mono)',
                fontSize: 9.5,
                lineHeight: 1.6,
                letterSpacing: '.05em',
                color: 'var(--gt-text-5)',
              }}
            >
              {v.intNudgeSub}
            </div>
            <PixelButton
              label={v.intNudgeCta}
              fs={8}
              pad="15px 12px"
              center
              lh="1"
              onClick={() => s.openCallRun(v.intNudgeIds)}
            />
          </div>
        ) : null}
        {v.sharedRows.map((r) =>
          r.show ? (
            <div
              key={r.key}
              style={{ display: 'flex', flexDirection: 'column', border: '1px solid ' + r.bd, background: 'var(--gt-card)' }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  background: r.labBg,
                  borderBottom: '1px solid var(--gt-border)',
                  padding: '9px 12px',
                }}
              >
                <div
                  style={{
                    fontFamily: 'var(--gt-font-pixel)',
                    fontSize: r.mFs,
                    color: r.bigClr,
                    textShadow: 'var(--gt-shadow-btn)',
                    transform: 'translateY(0.13em)',
                  }}
                >
                  {r.big}
                </div>
                <div style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.1em', color: 'var(--gt-text-5)' }}>
                  {r.sub}
                </div>
                <div style={{ flex: 1 }} />
                <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-faint)' }}>{r.cntTxt}</div>
              </div>
              <div data-hs="1" style={{ display: 'flex', gap: 10, overflowX: 'auto', padding: 12 }}>
                {r.games.map((g) => (
                  <Capsule key={g.key} g={g} />
                ))}
                {r.isEmpty ? (
                  <div
                    style={{
                      alignSelf: 'center',
                      fontFamily: MONO,
                      fontSize: 10,
                      color: 'var(--gt-ghost)',
                      padding: '14px 2px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    NOTHING HERE YET
                  </div>
                ) : null}
              </div>
            </div>
          ) : null,
        )}

        {/* NEEDS CALLS ---------------------------------------------------- */}
        {v.hasWaiting ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              border: '1px dashed var(--gt-border-3)',
              background: 'var(--gt-panel)',
              padding: '12px 14px',
            }}
          >
            <div style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: 7, color: 'var(--gt-text-5)' }}>NEEDS CALLS</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {v.waitGames.map((w) => (
                <span
                  key={w.key}
                  onClick={() => s.openGame(w.id)}
                  style={{
                    fontFamily: MONO,
                    fontSize: '9.5px',
                    color: 'var(--gt-text-5)',
                    border: '1px solid var(--gt-border)',
                    padding: '9px 10px',
                    cursor: 'pointer',
                  }}
                >
                  {w.nameUp} <span style={{ color: 'var(--gt-faint)' }}>· {w.cntTxt}</span>
                </span>
              ))}
            </div>
            <div style={{ fontFamily: MONO, fontSize: 9, lineHeight: 1.7, color: 'var(--gt-faint)' }}>
              ROW = EVERYONE&apos;S AVERAGE CALL (S3 · A2 · B1 · C0, ROUNDED) — A GAME WAITS HERE UNTIL HALF OF YOU HAVE CALLED IT
            </div>
          </div>
        ) : null}

        {v.tEmpty ? (
          <div
            style={{
              padding: '44px 20px',
              textAlign: 'center',
              fontFamily: MONO,
              fontSize: 11,
              color: 'var(--gt-muted)',
              lineHeight: 2,
            }}
          >
            {v.tEmptyTxt}
          </div>
        ) : null}
      </div>
    </div>
  );
}
